'use client';

import { c as _c } from "react/compiler-runtime";
import cx from 'classix';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { FaAngleRight, FaCircleCheck, FaCircleExclamation, FaCircleXmark, FaClock, FaLock } from 'react-icons/fa6';
import { MdClose, MdMenu } from 'react-icons/md';
import { useOnClickOutside } from 'usehooks-ts';
import CurrencySelect from '@/features/currency/components/currency-picker/currency-select/currency-select';
import { type CurrenciesList, type Currency } from '@/features/currency/types/currency-types';
import LanguageSelect from '@/features/language/components/language-picker/language-select/language-select';
import { type Language } from '@/features/language/types/language-types';
import { type UserListingsData } from '@/features/listing/types/user-listings-types';
import { type MessagesCounters } from '@/features/messages/types/message-counters-types';
import useAppFlyerLink from '@/features/roomster-app/hooks/use-app-flyer-link';
import { VerificationStatus } from '@/features/user/types/user-id-verification-types';
import { type LoggedUser, UserVerificationProvidersName } from '@/features/user/types/user-types';
import { usePathname } from '@/i18n/routing';
import Navigation from '@/layout/menu/navigation/navigation';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import { setSessionStorage } from '@/shared/utils/typed-session-storage';
import Avatar from '@/ui/avatar/avatar';
import Button from '@/ui/button/button';
import styles from './menu.module.scss';
import MenuLink from "@/layout/menu/menu-link";
interface MenuProps {
  loggedUser: LoggedUser | null;
  messagesCounters: MessagesCounters | null;
  userListingsData: UserListingsData | null;
  searchLink: string;
  languages: Language[];
  currencies: CurrenciesList;
  currentCurrency: Currency;
  megaphoneCount: number;
  isFirstListing?: boolean;
  hideLoginButton?: boolean;
}
export default function Menu(props) {
  const $ = _c(59);
  const {
    currencies,
    currentCurrency,
    isFirstListing,
    languages,
    loggedUser,
    megaphoneCount,
    messagesCounters,
    searchLink,
    userListingsData,
    hideLoginButton
  } = props;
  const menuRef = useRef(null);
  const pathname = usePathname();
  const translationsActions = useTranslations("global.actions");
  const translationsMenu = useTranslations("layout.menu");
  const translationsVerifications = useTranslations("global.verifications");
  const translationsEmailValidation = useTranslations("page.account-validation.email-validation");
  const loginAppFlyerLink = useAppFlyerLink("mobile", "mobileLoginButton", ROUTER_LINKS.phoneAuth);
  let isIdCheckRequired = false;
  if (loggedUser) {
    let t0;
    if ($[0] !== loggedUser.verifiedStatus.verifyProviders) {
      t0 = loggedUser.verifiedStatus.verifyProviders.find(_temp);
      $[0] = loggedUser.verifiedStatus.verifyProviders;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    const userIdVerifyStatus = t0;
    if (userIdVerifyStatus) {
      isIdCheckRequired = userIdVerifyStatus.mandatory && userIdVerifyStatus.verifiedDate === null;
    }
  }
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  let t0;
  if ($[2] !== loggedUser) {
    t0 = () => {
      if (loggedUser && !loggedUser.isPaidAccount) {
        setSessionStorage("paywallActionValue", "paywall_from_menu_upgrade_button");
      }
    };
    $[2] = loggedUser;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleClickUpgradeAccount = t0;
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setMobileMenuOpen(true);
      document.body.style.overflow = "hidden";
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const openMenu = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setMobileMenuOpen(false);
      document.body.style.overflow = "unset";
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const closeMenu = t2;
  useOnClickOutside(menuRef, closeMenu);
  let t3;
  let t4;
  if ($[6] !== isMobileMenuOpen) {
    t3 = () => {
      if (!isMobileMenuOpen) {
        document.body.style.overflow = "unset";
      }
    };
    t4 = [isMobileMenuOpen];
    $[6] = isMobileMenuOpen;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setMobileMenuOpen(false);
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== pathname) {
    t6 = [pathname];
    $[10] = pathname;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  if ($[12] !== loggedUser) {
    t7 = loggedUser ? <button className={styles.menu_userButton} onClick={openMenu}><div className={styles.menu_userAvatar}><Avatar imageSrc={loggedUser.avatar.pathSmall} alt={loggedUser.firstName} /></div><div className={styles.menu_userName}>{loggedUser.firstName}</div></button> : <div className={styles.menu_icon}><MdMenu onClick={openMenu} /></div>;
    $[12] = loggedUser;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = isMobileMenuOpen && styles.menu_background__isActive;
  let t9;
  if ($[14] !== t8) {
    t9 = cx(styles.menu_background, t8);
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <div className={t9} />;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = isMobileMenuOpen && styles.menu__isActive;
  let t12;
  if ($[18] !== t11) {
    t12 = cx(styles.menu, t11);
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== languages) {
    t13 = <div className={styles.languagePicker}><LanguageSelect options={languages} /></div>;
    $[20] = languages;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== currencies || $[23] !== currentCurrency) {
    t14 = <div className={styles.currencyPicker}><CurrencySelect currentCurrency={currentCurrency} currencies={currencies} /></div>;
    $[22] = currencies;
    $[23] = currentCurrency;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t13 || $[26] !== t14) {
    t15 = <div className={styles.settingsPickers}>{t13}{t14}</div>;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className={cx(styles.menu_icon, styles.close_menu)}><MdClose onClick={closeMenu} /></div>;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t15) {
    t17 = <div className={styles.menu_top}>{t15}{t16}</div>;
    $[29] = t15;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== handleClickUpgradeAccount || $[32] !== hideLoginButton || $[33] !== isFirstListing || $[34] !== isIdCheckRequired || $[35] !== loggedUser || $[36] !== loginAppFlyerLink || $[37] !== translationsActions || $[38] !== translationsEmailValidation || $[39] !== translationsMenu || $[40] !== translationsVerifications || $[41] !== userListingsData?.isAddressVerificationShow) {
    t18 = !isFirstListing && <>{!loggedUser && !hideLoginButton && <div className={styles.login}><Button href={loginAppFlyerLink} element="link" shade="dark" variant="filled" size="md" fullWidth={true}>{translationsActions("login")}</Button></div>}{loggedUser && <>{!loggedUser.isPaidAccount && <MenuLink className={cx(styles.menu_userInfoBlock, styles.upgradeAccount)} href={ROUTER_LINKS.accountPayments} onClick={handleClickUpgradeAccount} closeMenu={closeMenu}><div className={styles.upgradeAccount_avatar}><Avatar imageSrc={loggedUser.avatar.pathSmall} alt={loggedUser.firstName} /><div className={styles.upgradeAccount_avatarIcon}><FaLock className={styles.upgradeAccount_avatarIconSvg} /></div></div><div><div className={styles.upgradeAccount_title}>{translationsMenu("upgrade")}</div><div className={styles.upgradeAccount_description}>{translationsMenu("unlock-mailbox")}</div></div><FaAngleRight className={styles.menu_arrowIcon} /></MenuLink>}<MenuLink href={ROUTER_LINKS.accountValidation} className={cx(styles.menu_userInfoBlock, styles.userVerifications)} closeMenu={closeMenu}><Avatar imageSrc={loggedUser.avatar.pathSmall} alt={loggedUser.firstName} /><div className={styles.userVerifications_list}><div className={styles.userVerifications_item}><p>{translationsVerifications("phone-validated")}</p><FaCircleCheck className={cx(styles.userVerifications_icon, loggedUser.verifications.phone && styles.userVerifications_icon__isChecked)} /></div><div className={styles.userVerifications_item}><p>{loggedUser.verifications.email ? translationsVerifications("email-validated") : translationsEmailValidation("title")}</p>{loggedUser.verifications.email ? <FaCircleCheck className={cx(styles.userVerifications_icon, styles.userVerifications_icon__isChecked)} /> : <span style={{
                width: "18px"
              }} />}</div>{(loggedUser.verifications.email || isIdCheckRequired) && loggedUser.verifications.id && <div className={styles.userVerifications_item}>{loggedUser.verifications.id.status === VerificationStatus.New && <><p>{translationsVerifications("get-id-check")}</p><span style={{
                  width: "18px"
                }} /></>}{loggedUser.verifications.id.status === VerificationStatus.Passed && <><p>{translationsVerifications("id-check-passed")}</p><FaCircleCheck className={cx(styles.userVerifications_icon, styles.userVerifications_icon__isChecked)} /></>}{loggedUser.verifications.id.status === VerificationStatus.Rejected && <><p>{translationsVerifications("id-check-rejected")}</p><FaCircleXmark className={cx(styles.userVerifications_icon, styles.userVerifications_icon__isRejected)} /></>}{loggedUser.verifications.id.status === VerificationStatus.Error && <><p>{translationsVerifications("id-check-error")}</p><FaCircleExclamation className={cx(styles.userVerifications_icon, styles.userVerifications_icon__isError)} /></>}{loggedUser.verifications.id.status === VerificationStatus.Pending && <><p>{translationsVerifications("id-check-pending")}</p><FaClock className={cx(styles.userVerifications_icon, styles.userVerifications_icon__isPending)} /></>}</div>}{loggedUser.verifications.email && userListingsData?.isAddressVerificationShow && <div className={styles.userVerifications_item}><p>{translationsVerifications("get-address-check")}</p><span style={{
                width: "18px"
              }} /></div>}</div><FaAngleRight className={styles.menu_arrowIcon} /></MenuLink></>}</>;
    $[31] = handleClickUpgradeAccount;
    $[32] = hideLoginButton;
    $[33] = isFirstListing;
    $[34] = isIdCheckRequired;
    $[35] = loggedUser;
    $[36] = loginAppFlyerLink;
    $[37] = translationsActions;
    $[38] = translationsEmailValidation;
    $[39] = translationsMenu;
    $[40] = translationsVerifications;
    $[41] = userListingsData?.isAddressVerificationShow;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== isFirstListing || $[44] !== loggedUser || $[45] !== megaphoneCount || $[46] !== messagesCounters || $[47] !== searchLink || $[48] !== userListingsData) {
    t19 = <Navigation user={loggedUser} messagesCounters={messagesCounters} megaphoneCount={megaphoneCount} userListingsData={userListingsData} closeMenu={closeMenu} searchLink={searchLink} isFirstListing={isFirstListing} />;
    $[43] = isFirstListing;
    $[44] = loggedUser;
    $[45] = megaphoneCount;
    $[46] = messagesCounters;
    $[47] = searchLink;
    $[48] = userListingsData;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t12 || $[51] !== t17 || $[52] !== t18 || $[53] !== t19) {
    t20 = <div ref={menuRef} className={t12}>{t17}{t18}{t19}</div>;
    $[50] = t12;
    $[51] = t17;
    $[52] = t18;
    $[53] = t19;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== t10 || $[56] !== t20 || $[57] !== t7) {
    t21 = <>{t7}{t10}{t20}</>;
    $[55] = t10;
    $[56] = t20;
    $[57] = t7;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  return t21;
}
function _temp(t0) {
  const {
    name
  } = t0;
  return UserVerificationProvidersName.ID === name;
}